import { Camera } from '@babylonjs/core/Cameras/camera';
import { BoundingSphere } from '@babylonjs/core/Culling/boundingSphere';
import { Ray } from '@babylonjs/core/Culling/ray';
import { Plane } from '@babylonjs/core/Maths/math.plane';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Scene } from '@babylonjs/core/scene';
import { SketchControlPoint, SketchPath, SpaceNode, Token, getByDynamicId } from '@models/classes';
import { eAxis, eSketchPathType } from '@models/classes/enums';
import { SketchNode } from '@models/classes/meshes/sketch-node';
import { KbColor } from '@models/classes/primitives/kb-color';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { roundVectorToPlaces } from '@view/helpers/math-helper';
import { createSketchPointForEditor, getSketchNormalVector3 } from '@view/modules/sketching/util';
import { CONNECTOR_DIAM } from '@view/render/features/connector';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { KbViewer } from '../kb-viewer';
import { KbArcRotateCamera } from './cameras/kb-arc-rotate-camera';

const DefaultSketchOptions = {
    faceCamera: true,
    snapSize: 0.5,
};

let activeHitPoint: SketchControlPoint | undefined;
let sketchPlaneIntersect: KbVector;

export type ISketchOptions = Partial<typeof DefaultSketchOptions>;

export class SketchEditor {
    mouseUp = new Subject<void>();

    constructor(private kbViewer: KbViewer, private scene: Scene, private defaultHighlightColor: KbColor) {}

    private currentOptions: Required<ISketchOptions> | undefined;
    private end = new Subject<void>();
    private sketchPoints: SketchControlPoint[] = [];
    private activeSketch: SketchNode | undefined;

    updateActiveOptions(options: ISketchOptions) {
        if (this.currentOptions) {
            for (const prop in options) {
                // @ts-expect-error typing not smart enough to know that option objects are the same
                this.currentOptions[prop] = options[prop];
            }
        }
    }

    start(sketch: SketchNode) {
        let origin = KbVector.Zero();
        for (let i = 0; i < sketch.paths.length; i++) {
            const path = sketch.paths[i];
            if (path.controlPoints.length > 0) {
                origin = path.controlPoints[0].position;
                break;
            }
        }

        return this.startOnPlane(
            sketch,
            {
                normal: sketch.normal,
                intersect: origin,
            },
            undefined,
            true
        );
    }

    startOnPlane(
        sketch: SketchNode,
        plane: { normal: KbVector | eAxis; intersect: KbVector },
        userOptions?: ISketchOptions,
        useSketchNormal = false
    ) {
        this.activeSketch = sketch;
        sketchPlaneIntersect = plane.intersect;
        const currentCanvas = this.kbViewer.canvas!;
        const options: Required<ISketchOptions> = { ...DefaultSketchOptions, ...userOptions };
        this.currentOptions = options;
        this.sketchPoints = sketch.paths.reduce((result, path) => {
            for (const point of path.controlPoints) {
                point._visible = true;
                result.push(point);
            }
            return result;
        }, [] as SketchControlPoint[]);

        const previewVisibleOriginal = sketch._previewVisible;
        sketch._previewVisible = true;
        let planeNormal = getSketchNormalVector3(plane.normal);
        if (useSketchNormal) {
            const sketchNode = this.kbViewer.getRendererNode(sketch);
            if (sketchNode) {
                const matrix = sketchNode.getWorldMatrix().getRotationMatrix();
                planeNormal = Vector3.TransformCoordinates(planeNormal, matrix);
            }
        }
        const planeIntersect = plane.intersect.toVec3();
        const sketchPlane = Plane.FromPositionAndNormal(planeIntersect, planeNormal);

        const originalCamera = this.scene.activeCamera!;

        let sketchCamera: KbArcRotateCamera | undefined;
        if (options.faceCamera) {
            originalCamera.detachControl();
            // camera.storeCameraSettings();
            // const viewpoint = new ViewpointNode()
            // camera.moveCameraToViewpoint(viewpoint);
            sketchCamera = new KbArcRotateCamera('sketchEditorView', 0, 0, 10, planeIntersect, this.scene);
            sketchCamera.mode = Camera.ORTHOGRAPHIC_CAMERA;
            sketchCamera.lockRotation = true;
            sketchCamera.wheelPrecision = 60;
            sketchCamera.pinchPrecision = 60;
            switch (plane.normal) {
                case eAxis.x:
                case eAxis.z:
                    sketchCamera.upVector = Vector3.Up();
                    break;
                case eAxis.y:
                    sketchCamera.upVector = Vector3.Backward();
                    break;
                default:
                    sketchCamera.upVector = Vector3.Cross(
                        planeNormal,
                        planeNormal.equalsToFloats(0, 1, 0) ? Vector3.Right() : Vector3.Up()
                    );
            }
            sketchCamera.setPosition(planeNormal.scale(5000).add(planeIntersect));
            sketchCamera.orthoFit(10, 10);

            this.scene.activeCamera = sketchCamera;
            currentCanvas.focus();
            sketchCamera.attachControl();
            this.kbViewer.viewChanged.next();
        }

        const currentCamera = this.scene.activeCamera!;

        interface DragInfo {
            point: SketchControlPoint;
            handle: 'position' | 'direction' | 'direction2';
        }
        let dragObjects: DragInfo[] = [];
        let lastMousePosition: Vector3 | null = null;
        const sketchParent = sketch._parent ? this.kbViewer.getRendererNode(sketch._parent) : undefined;

        this.kbViewer.interaction.onPointerDown.pipe(takeUntil(this.end)).subscribe(e => {
            if (e.button === 0 && e.pickInfo && e.pickInfo.ray) {
                const deletedPoints: SketchControlPoint[] = [];
                const ray = e.pickInfo.ray;
                lastMousePosition = getPointOnPlane(ray);
                if (lastMousePosition) {
                    roundVectorToPlaces(lastMousePosition, options.snapSize);
                }

                const v1 = new Vector3(-1, -1, -1),
                    v2 = new Vector3(1, 1, 1),
                    collisionSphere = new BoundingSphere(v1, v2);

                const r = CONNECTOR_DIAM / 2;

                for (let i = 0; i < this.sketchPoints.length; i++) {
                    const point = this.sketchPoints[i];
                    const pointRendered = this.kbViewer.getRendererNode(point);
                    if (!pointRendered || pointRendered.isDisposed()) {
                        this.sketchPoints.splice(i, 1);
                        i--;
                        continue;
                    }

                    const cpCenter = pointRendered.position.clone();
                    const cpHandle1 = cpCenter.add(point.direction.toVec3());
                    const cpHandle2 = point.direction2 ? cpCenter.add(point.direction2.toVec3()) : undefined;
                    if (sketchParent) {
                        const matrix = sketchParent.computeWorldMatrix();
                        Vector3.TransformCoordinatesToRef(cpCenter, matrix, cpCenter);
                        Vector3.TransformCoordinatesToRef(cpHandle1, matrix, cpHandle1);
                        if (cpHandle2) {
                            Vector3.TransformCoordinatesToRef(cpHandle2, matrix, cpHandle2);
                        }
                    }
                    v1.copyFrom(cpCenter).addInPlaceFromFloats(-r, -r, -r);
                    v2.copyFrom(cpCenter).addInPlaceFromFloats(r, r, r);
                    collisionSphere.reConstruct(v1, v2);
                    let hit = ray.intersectsSphere(collisionSphere);
                    let handle: DragInfo['handle'] = 'position';

                    if (!hit) {
                        v1.copyFrom(cpHandle1).addInPlaceFromFloats(-r, -r, -r);
                        v2.copyFrom(cpHandle1).addInPlaceFromFloats(r, r, r);
                        collisionSphere.reConstruct(v1, v2);
                        hit = ray.intersectsSphere(collisionSphere);
                        handle = 'direction';
                    }
                    if (!hit && cpHandle2) {
                        v1.copyFrom(cpHandle2).addInPlaceFromFloats(-r, -r, -r);
                        v2.copyFrom(cpHandle2).addInPlaceFromFloats(r, r, r);
                        collisionSphere.reConstruct(v1, v2);
                        hit = ray.intersectsSphere(collisionSphere);
                        handle = 'direction2';
                    }

                    if (hit) {
                        dragObjects.push({ point, handle });

                        // If ctrl key is held down, allow dragging just one point so you can separate paths
                        if (e.ctrlKey) {
                            break;
                        }
                    }
                }
                deletedPoints.forEach(point => {
                    const index = this.sketchPoints.findIndex(ec => ec.id === point.id);
                    this.sketchPoints.splice(index, 1);
                });

                // If both direction handles and positions are hit, only drag the positions and leave the handles alone
                if (dragObjects.find(d => d.handle === 'position')) {
                    dragObjects = dragObjects.filter(d => d.handle === 'position');
                }

                if (dragObjects.length === 0) {
                    activeHitPoint = undefined;
                } else {
                    activeHitPoint = dragObjects[0].point;
                    currentCamera.detachControl();
                }
            }
        });

        this.kbViewer.interaction.onPointerUp.pipe(takeUntil(this.end)).subscribe(e => {
            dragObjects = [];
            this.mouseUp.next();
            currentCamera.attachControl(currentCanvas);
        });

        this.kbViewer.interaction.onPointerMove.pipe(takeUntil(this.end)).subscribe(e => {
            if (dragObjects.length > 0 && e.pickInfo && e.pickInfo.ray) {
                const ray = e.pickInfo.ray;
                const intersect = getPointOnPlane(ray);
                if (intersect) {
                    if (sketchParent) {
                        const transformInv = sketchParent.computeWorldMatrix().clone().invert();
                        Vector3.TransformCoordinatesToRef(intersect, transformInv, intersect);
                    }
                    roundVectorToPlaces(intersect, options.snapSize);
                    dragObjects.forEach(dragObject => {
                        if (dragObject.handle === 'position') {
                            roundVectorToPlaces(intersect, options.snapSize);
                            dragObject.point.position = KbVector.FromVec3(intersect);
                        } else {
                            const movement = intersect.subtract(dragObject.point.position.toVec3());
                            roundVectorToPlaces(intersect, options.snapSize);

                            if (dragObject.handle === 'direction') {
                                dragObject.point.direction = KbVector.FromVec3(movement);
                            } else if (dragObject.point.direction2) {
                                dragObject.point.direction2 = KbVector.FromVec3(movement);
                            }
                        }
                    });
                }
            }
        });

        this.kbViewer.interaction.onClick.pipe(takeUntil(this.end)).subscribe(e => {
            if (dragObjects.length === 0 && e.pickInfo) {
                if (e.button === 2 && e.canvasX && e.canvasY) {
                    if (e.pickInfo.pickedMesh instanceof LinesMesh && e.pickInfo.pickedPoint) {
                        this.kbViewer.rightClickMenu.showContextMenu(
                            e.canvasX,
                            e.canvasY,
                            this.getRightClickButtonsForLines(sketch, e.pickInfo.faceId, e.pickInfo.pickedPoint)
                        );
                    } else if (e.pickInfo.pickedMesh instanceof Mesh) {
                        const kb3dNode = getByDynamicId(e.pickInfo.pickedMesh.id);
                        if (kb3dNode instanceof SketchControlPoint) {
                            activeHitPoint = kb3dNode;
                            this.kbViewer.rightClickMenu.showContextMenu(
                                e.canvasX,
                                e.canvasY,
                                this.getRightClickButtons()
                            );
                        }
                    }
                } else if (e.button === 0 && e.pickInfo.ray) {
                    if (this.kbViewer.rightClickMenu.isOpen()) {
                        this.kbViewer.rightClickMenu.hideContextMenu();
                        return;
                    }
                    const ray = e.pickInfo.ray;
                    const intersect = getPointOnPlane(ray);
                    if (intersect) {
                        const manager = sketch._manager!;
                        roundVectorToPlaces(intersect, options.snapSize);

                        let lastPath = sketch.paths.length > 0 ? sketch.paths[sketch.paths.length - 1] : undefined;
                        if (!lastPath) {
                            lastPath = manager.create(Token.SketchPath);
                            lastPath.name = 'Path';
                            sketch.addChildren(lastPath);
                        }

                        const c = createSketchPointForEditor(manager, lastPath);

                        if (sketch._parent instanceof SpaceNode) {
                            const rendererNode = this.kbViewer.getRendererNode(sketch);

                            if (rendererNode) {
                                Vector3.TransformCoordinatesToRef(
                                    intersect,
                                    rendererNode.computeWorldMatrix().clone().invert(),
                                    intersect
                                );
                            }
                            c.position = KbVector.FromVec3(intersect);
                        }

                        this.sketchPoints.push(c);
                    }
                }
            }
        });

        this.end.pipe(take(1)).subscribe(() => {
            if (!previewVisibleOriginal) {
                sketch.paths.forEach(path => {
                    for (const point of path.controlPoints) {
                        point._visible = false;
                    }
                });
            }

            originalCamera.attachControl(currentCanvas);
            this.scene.activeCamera = originalCamera;
            if (sketchCamera) {
                sketchCamera.dispose();
            }
            sketch._previewVisible = previewVisibleOriginal;
        });

        function getPointOnPlane(ray: Ray) {
            const d = ray.intersectsPlane(sketchPlane);
            if (typeof d === 'number') {
                return ray.origin.add(ray.direction.scale(d));
            }
            return null;
        }
    }

    clear() {
        this.end.next();
        this.kbViewer.rightClickMenu.hideContextMenu();
        this.currentOptions = undefined;
        this.activeSketch = undefined;
    }

    dispose() {
        this.kbViewer.rightClickMenu.hideContextMenu();
        this.end.next();
        this.end.complete();
        this.mouseUp.complete();
        this.activeSketch = undefined;
    }

    private deleteSketchPoint() {
        if (this.kbViewer.sceneNode._manager && activeHitPoint) {
            const path = activeHitPoint._parent;
            const targetPoint = activeHitPoint;
            if (path instanceof SketchPath) {
                const index = path.controlPoints.findIndex(ec => ec.id === targetPoint.id);
                path.controlPoints.splice(index, 1);
            }
        }
    }

    private deleteSketchPath() {
        if (this.kbViewer.sceneNode._manager && activeHitPoint) {
            if (this.activeSketch) {
                const sketch = this.activeSketch;
                const targetPoint = activeHitPoint;
                sketch.paths.forEach(path => {
                    if (path instanceof SketchPath) {
                        const index = path.controlPoints.findIndex(
                            ec => ec._dynamicId === targetPoint._parent?._dynamicId
                        );
                        sketch.paths.splice(index, 1);
                    }
                });
            }
        }
    }

    private separateSketchNode(sketchpathType: eSketchPathType) {
        if (this.kbViewer.sceneNode._manager && activeHitPoint) {
            // let targetControlPoint: SketchControlPoint | undefined;
            const manager = this.kbViewer.sceneNode._manager;

            if (this.activeSketch) {
                const sketch = this.activeSketch;
                const targetPoint = activeHitPoint;
                for (let pathIndex = 0; pathIndex < sketch.paths.length; pathIndex++) {
                    const path = sketch.paths[pathIndex];
                    const cpIndex = path.controlPoints.findIndex(ec => ec._dynamicId === targetPoint._dynamicId);
                    if (cpIndex === -1) {
                        continue;
                    }
                    if (path.type === sketchpathType) {
                        return;
                    }

                    if (path.controlPoints.length === 1 && sketchpathType === eSketchPathType.arcThrough3Points) {
                        const newControlPoint2 = createSketchPointForEditor(manager, path);
                        const newControlPoint3 = createSketchPointForEditor(manager, path);

                        let sketchNormal = new Vector3(1, 0, 0);
                        if (sketch.normal === eAxis.y) {
                            sketchNormal = new Vector3(0, 1, 0);
                        } else if (sketch.normal === eAxis.z) {
                            sketchNormal = new Vector3(0, 0, 1);
                        }
                        const controlPointMesh = this.kbViewer.getRendererNode(
                            sketch.paths[pathIndex].controlPoints[0]
                        );
                        const pointOnePosition = sketchNormal.cross(
                            sketchPlaneIntersect.toVec3().subtract(controlPointMesh.getAbsolutePosition())
                        );
                        newControlPoint2.position = new KbVector(
                            pointOnePosition.x,
                            pointOnePosition.y,
                            pointOnePosition.z
                        );
                        const pointTwoPosition = sketchNormal.cross(
                            pointOnePosition.subtract(controlPointMesh.getAbsolutePosition())
                        );
                        newControlPoint3.position = new KbVector(
                            pointTwoPosition.x,
                            pointTwoPosition.y,
                            pointTwoPosition.z
                        );

                        path._expanded = true;
                        path.type = sketchpathType;
                        this.sketchPoints.push(newControlPoint2);
                        this.sketchPoints.push(newControlPoint3);
                        break;
                    } else if (cpIndex === 0 && sketch.paths[pathIndex - 1]?.type === sketchpathType) {
                        // If an adjacent path has the same path type as the one we're making, we'll just add the control point to it
                        // Previous path matching case
                        const cp = path.controlPoints.shift()!;

                        sketch.paths[pathIndex - 1].controlPoints.push(cp.clone()!);
                        break;
                    } else if (
                        // Next path matching case
                        cpIndex === path.controlPoints.length - 1 &&
                        sketch.paths[pathIndex + 1]?.type === sketchpathType
                    ) {
                        const cp = path.controlPoints.pop()!;
                        sketch.paths[pathIndex + 1].controlPoints.push(cp.clone()!);
                        break;
                    } else if (path.controlPoints.length > 1) {
                        let selectedControlPoints: SketchControlPoint[];
                        let previousPath: SketchPath | undefined;
                        let selectedPath: SketchPath;
                        let nextPath: SketchPath | undefined;

                        if (cpIndex === 0) {
                            selectedPath = path;
                        } else {
                            previousPath = path;
                            selectedControlPoints = Array.from(
                                previousPath.controlPoints.splice(cpIndex, path.controlPoints.length - cpIndex)
                            );

                            selectedPath = manager.create(Token.SketchPath);
                            sketch.paths.splice(pathIndex + 1, 0, selectedPath);
                            selectedPath.name = 'Path';
                            selectedControlPoints.reverse();
                            while (selectedControlPoints.length > 0) {
                                const cp = manager.clone(selectedControlPoints.pop()!);
                                cp._visible = true;
                                selectedPath.controlPoints.push(cp);
                                this.sketchPoints.push(cp);
                            }
                        }

                        if (selectedPath.controlPoints.length > 2) {
                            let nextPathPoints: SketchControlPoint[];
                            if (
                                sketchpathType == eSketchPathType.arcThrough3Points &&
                                selectedPath.controlPoints.length > 3
                            ) {
                                nextPathPoints = Array.from(
                                    selectedPath.controlPoints.splice(3, selectedPath.controlPoints.length - 3)
                                );
                            } else {
                                nextPathPoints = Array.from(
                                    selectedPath.controlPoints.splice(1, selectedPath.controlPoints.length - 1)
                                );
                            }

                            nextPath = manager.create(Token.SketchPath);
                            sketch.paths.splice(pathIndex + 2, 0, nextPath);
                            nextPath.name = 'Path';
                            nextPathPoints.reverse();
                            while (nextPathPoints.length > 0) {
                                const cp = manager.clone(nextPathPoints.pop()!);
                                cp._visible = true;
                                nextPath.controlPoints.push(cp);
                                this.sketchPoints.push(cp);
                            }
                        }

                        selectedPath._expanded = true;
                        selectedPath.type = sketchpathType;

                        if (previousPath) {
                            const match = manager.clone(selectedPath.controlPoints[0]);
                            match._visible = true;
                            previousPath.controlPoints.push(match);
                            this.sketchPoints.push(match);
                        }
                        if (nextPath && nextPath.controlPoints.length > 0) {
                            const match = manager.clone(nextPath.controlPoints[0]);
                            match._visible = true;
                            selectedPath.controlPoints.push(match);
                            this.sketchPoints.push(match);
                        }

                        break;
                    }
                }
            }
        }
    }

    private addSketchNodeOnPath(node: SketchNode, faceId: number, pickedPoint: Vector3) {
        const index = node._lineSegmentStartingPoints[faceId];
        let counter = 0;
        const manager = node._manager!;

        for (let pathIndex = 0; pathIndex < node.paths.length; pathIndex++) {
            const path = node.paths[pathIndex];
            for (let cpIndex = 0; cpIndex < node.paths.length; cpIndex++) {
                const controlPoint = path.controlPoints[cpIndex];
                if (counter === index) {
                    const spliceIndex = path.controlPoints.indexOf(controlPoint);
                    const sketchControlPoint = manager.create(Token.SketchControlPoint);
                    sketchControlPoint.position = KbVector.FromVec3(pickedPoint);
                    path.controlPoints.splice(spliceIndex + 1, 0, sketchControlPoint);
                    sketchControlPoint._visible = true;
                    sketchControlPoint._expanded = true;
                    this.sketchPoints.push(sketchControlPoint);
                    return;
                }
                counter++;
            }
        }
    }

    private getRightClickButtons() {
        return [
            { label: 'delete node', callback: () => this.deleteSketchPoint() },
            { label: 'delete path', callback: () => this.deleteSketchPath() },
            { label: 'convert to cubic bezier', callback: () => this.separateSketchNode(eSketchPathType.cubicBezier) },
            {
                label: 'convert to quadratic bezier',
                callback: () => this.separateSketchNode(eSketchPathType.quadraticBezier),
            },
            { label: 'convert to hermite', callback: () => this.separateSketchNode(eSketchPathType.hermite) },
            { label: 'convert to catmullrom', callback: () => this.separateSketchNode(eSketchPathType.catmullRom) },
            { label: 'convert to points', callback: () => this.separateSketchNode(eSketchPathType.points) },
            {
                label: 'arc through 3 points',
                callback: () => this.separateSketchNode(eSketchPathType.arcThrough3Points),
            },
        ];
    }

    private getRightClickButtonsForLines(node: SketchNode, faceId: number, pickedPoint: Vector3) {
        return [
            {
                label: 'add point on line',
                callback: () => this.addSketchNodeOnPath(node, faceId, pickedPoint),
            },
        ];
    }
}
